import { __spreadArray } from "tslib";
import deburr from 'lodash-es/deburr';
import { config } from '../config';
// Reuse the app code (`gaid`) as an id namespace so ids do not collide with
// the host page the bubble is embedded into. `config` is undefined under
// NODE_ENV=testing, hence the literal fallback.
var ID_PREFIX = (config && config.appCode) || 'gaid';
/**
 * Normalize a value into a DOM-id-safe slug: strip diacritics, lowercase, and
 * collapse every run of non-alphanumeric characters into a single hyphen.
 *
 * Unlike lodash `kebabCase`, this does NOT insert hyphens between letters and
 * digits, so opaque tokens (e.g. `fdac8c6c`) stay compact instead of exploding
 * into `fdac-8-c-6-c`.
 */
var slug = function (value) {
    return deburr("".concat(value))
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};
/** Slugify each part, drop the empty ones, and join with hyphens. */
var slugParts = function (parts) {
    return parts
        .map(function (p) { return (p === null || p === undefined ? '' : slug(p)); })
        .filter(Boolean)
        .join('-');
};
/**
 * Build a stable, collision-safe DOM id from parts (prefixed with the app code).
 *
 * @example buildElementId('header', 'close')               // 'gaid-header-close'
 * @example buildElementId('message-option', 'Ďakujeme', 0) // 'gaid-message-option-dakujeme-0'
 */
export var buildElementId = function () {
    var parts = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        parts[_i] = arguments[_i];
    }
    var body = slugParts(parts);
    return body ? "".concat(ID_PREFIX, "-").concat(body) : ID_PREFIX;
};
/**
 * Build the spreadable test/identity attributes for an interactive element:
 * a namespaced `id` (collision-safe on the host page) plus a prefix-free
 * `data-testid` (the stable selector used by Cypress tests). Both are derived
 * from the same parts, so they can never drift out of sync.
 *
 * @example <button {...elementAttrs('generic-form', 'submit')} />
 * //  id="gaid-generic-form-submit" data-testid="generic-form-submit"
 */
export var elementAttrs = function () {
    var parts = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        parts[_i] = arguments[_i];
    }
    var body = slugParts(parts);
    return {
        id: body ? "".concat(ID_PREFIX, "-").concat(body) : ID_PREFIX,
        'data-testid': body || ID_PREFIX,
    };
};
/**
 * Prefix-free slug string, for containers/elements that only need a
 * `data-testid` (no namespaced `id`).
 *
 * @example <div data-testid={testId('generic-form', scope)} />  // "generic-form-email"
 */
export var testId = function () {
    var parts = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        parts[_i] = arguments[_i];
    }
    return slugParts(parts) || ID_PREFIX;
};
/**
 * Like `elementAttrs`, but the `index` is appended to the `id` ONLY —
 * `data-testid` stays meaningful and content-scoped. For widgets with no stable
 * config field to scope by (NPS, ratings, tiles, quick-reply option buttons):
 * `index` is a stable per-widget-type occurrence index, so DOM `id`s stay unique
 * while tests still select the meaningful `data-testid` within a content-scoped
 * message.
 *
 * @example <button {...indexedAttrs(2, 'nps', 'submit')} />
 * //  id="gaid-nps-submit-2"  data-testid="nps-submit"
 */
export var indexedAttrs = function (index) {
    var parts = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        parts[_i - 1] = arguments[_i];
    }
    var testidBody = slugParts(parts);
    var idBody = slugParts(__spreadArray(__spreadArray([], parts, true), [index], false));
    return {
        id: idBody ? "".concat(ID_PREFIX, "-").concat(idBody) : ID_PREFIX,
        'data-testid': testidBody || ID_PREFIX,
    };
};
